import { Link } from "react-router-dom";
import { PASSED_MEETING_NOTE } from "@/lib/meeting-labels";
import { meetingKindLabel } from "@/lib/meeting-calendar";
import type { WorkQueueItem, WorkQueueLane } from "@care-by-ella/contracts";
import { DOCTOR_WORK_QUEUE_LANES } from "@care-by-ella/contracts";
import { PatientLabel } from "@/components/PatientLabel";
import { useReadOnly, useViewPath } from "@/components/SupportView";
import { patientMeetingHref } from "@/lib/patient-links";
import { activeItems } from "@/lib/lab-result-check";
import {
  byUrgency,
  LANE_PATH,
  laneItems,
  meetingsToDocument,
  todaysMeetings,
  unreadChatCount,
  useClinicQueues,
} from "@/lib/clinic-queues";
import { LANE_LABEL } from "./WorkQueue";
import { NextMeetingCard } from "@/components/meeting-calendar/NextMeetingCard";

// TES-506 — "Idag", the doctor's start page.
//
// The 2026-09-04 walkthrough, the lines that steer the whole page:
//   - The doctor's priority, what I must see: upcoming meetings.
//   - Add a dashboard that shows the most urgent item from each part.
//   - Doctors should only see what is on their plate today.
//
// So: today's meetings at the top, then ONE row per category — the number
// waiting and the most urgent case in it, with the way on to the category's own
// page. The page never acts on its own; it points. Deciding a renewal here
// would have turned the dashboard into a fourth inbox, which is exactly what
// the walkthrough tore out.

const PRIORITY_LABEL: Record<string, string> = {
  low: "Låg",
  medium: "Medel",
  high: "Hög",
  urgent: "Akut",
};

const PRIORITY_CLASS: Record<string, string> = {
  low: "bg-surfaceMuted text-textSecondary",
  medium: "bg-brand/10 text-brand",
  high: "bg-warning/15 text-warning",
  urgent: "bg-danger/10 text-danger",
};

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("sv-SE", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function CategoryRow({
  label,
  count,
  to,
  top,
}: {
  label: string;
  count: number;
  to: string;
  top: WorkQueueItem | null;
}) {
  // TES-966 — on a phone the row is count, lane and arrow; the top item's
  // title had no room left beside a 12rem label and wrapped a word per line.
  return (
    <Link
      to={to}
      className="flex items-center gap-md rounded-md border border-surfaceMuted bg-surface p-md transition hover:border-brand"
    >
      <span className="flex h-8 min-w-8 items-center justify-center rounded-full bg-brand/10 px-xs text-sm font-medium text-brand">
        {count}
      </span>
      <span className="min-w-0 flex-1 text-sm font-medium sm:w-48 sm:flex-none">
        {label}
      </span>
      {top ? (
        <>
          <span
            className={`rounded-pill px-sm py-xs text-xs ${PRIORITY_CLASS[top.priority] ?? ""}`}
          >
            {PRIORITY_LABEL[top.priority] ?? top.priority}
          </span>
          <span className="hidden min-w-0 flex-1 truncate text-sm text-textSecondary sm:block">
            {top.title} ·{" "}
            <PatientLabel
              patientId={top.patientId}
              name={top.patientName}
              personalNumberLast6={top.personalNumberLast6}
            />
          </span>
        </>
      ) : (
        <span className="hidden flex-1 text-sm text-textSecondary sm:block">
          Inget att göra just nu.
        </span>
      )}
      <span className="shrink-0 text-sm text-brand">→</span>
    </Link>
  );
}

export function TodayRoute() {
  const { workQueue, meetings, chat, loading } = useClinicQueues();
  // TES-1040 — in the admin's support view: links stay in the view, no chat.
  const viewPath = useViewPath();
  const readOnly = useReadOnly();

  const today = todaysMeetings(meetings);
  const toDocument = meetingsToDocument(meetings);
  const unread = unreadChatCount(chat);

  // Server-decided lanes when we have them; the static doctor list before the
  // first response lands, so the page renders its full shape while loading
  // instead of growing rows under the reader.
  //
  // TES-1037 — minus onboarding: a submitted health declaration is not a task
  // of its own for the doctor, so its count must not sit in "Det mest akuta"
  // any more than in the nav. She meets the answers in the meeting, the
  // transfer review and the patient overview.
  const lanes: readonly WorkQueueLane[] = (
    workQueue ? workQueue.lanes.map((l) => l.lane) : DOCTOR_WORK_QUEUE_LANES
  ).filter((lane) => lane !== "onboarding");

  return (
    <div className="flex flex-col gap-lg">
      <div>
        <h1 className="font-display text-3xl tracking-tight">Idag</h1>
        <p className="mt-xs text-sm text-textSecondary">
          {new Date().toLocaleDateString("sv-SE", {
            weekday: "long",
            day: "numeric",
            month: "long",
          })}
        </p>
      </div>

      {/* ---- TES-1233: the next meeting, always on top ---- */}
      <NextMeetingCard meetings={meetings} loading={loading} />

      {/* ---- Today's meetings ---- */}
      <section className="flex flex-col gap-sm">
        <h2 className="font-display text-lg tracking-tight">Dagens möten</h2>
        {loading && !meetings ? (
          <p className="text-sm text-textSecondary">Hämtar…</p>
        ) : today.length === 0 ? (
          <div className="rounded-md border border-surfaceMuted bg-surface p-md text-sm text-textSecondary">
            Inga möten inbokade idag.
          </div>
        ) : (
          <ul className="flex flex-col gap-xs">
            {today.map((m) => (
              <li
                key={m.id}
                className="flex flex-wrap items-center gap-md rounded-md border border-surfaceMuted bg-surface p-md lg:flex-nowrap"
              >
                <span className="w-14 shrink-0 font-medium">
                  {fmtTime(m.scheduledAt)}
                </span>
                <span className="min-w-0 flex-1 basis-40 truncate text-sm lg:basis-0">
                  <PatientLabel
                    patientId={m.patient.id}
                    name={m.patient.name}
                    personalNumberLast6={m.patient.personalNumberLast6}
                    link
                  />
                  <span className="text-textSecondary">
                    {" "}
                    {/* TES-1233 — Emelie's meeting types. */}·{" "}
                    {meetingKindLabel(m)}
                  </span>
                </span>
                <Link
                  to={viewPath(patientMeetingHref(m.patient.id, m.id))}
                  className="shrink-0 rounded-md bg-brand px-md py-xs text-sm text-textOnBrand hover:opacity-90"
                >
                  Öppna mötet
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ---- To document: past meetings not yet written up ---- */}
      {toDocument.length > 0 ? (
        <section className="flex flex-col gap-sm">
          <h2 className="font-display text-lg tracking-tight">
            Att dokumentera
          </h2>
          {/* TES-1183 (TES-1180 point 8) — the old line said the meeting
              counted as held only once documented; in fact the onboarding
              step closes when the time has passed (A-02 2026-10-08 keeps
              that), and a passed meeting decides nothing. Proposed copy. */}
          <p className="text-sm text-textSecondary">{PASSED_MEETING_NOTE}</p>
          <ul className="flex flex-col gap-xs">
            {toDocument.map((m) => (
              <li
                key={m.id}
                className="flex flex-wrap items-center gap-md rounded-md border border-brand/40 bg-surface p-md lg:flex-nowrap"
              >
                <span className="w-32 shrink-0 text-sm">
                  {fmtDay(m.scheduledAt)} {fmtTime(m.scheduledAt)}
                </span>
                <span className="min-w-0 flex-1 basis-40 truncate text-sm lg:basis-0">
                  <PatientLabel
                    patientId={m.patient.id}
                    name={m.patient.name}
                    personalNumberLast6={m.patient.personalNumberLast6}
                    link
                  />
                </span>
                <Link
                  to={viewPath(patientMeetingHref(m.patient.id, m.id))}
                  className="shrink-0 rounded-md bg-brand px-md py-xs text-sm text-textOnBrand hover:opacity-90"
                >
                  {readOnly ? "Öppna mötet" : "Dokumentera"}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* ---- The most urgent item from each category ---- */}
      <section className="flex flex-col gap-sm">
        <h2 className="font-display text-lg tracking-tight">
          Det mest akuta just nu
        </h2>
        <div className="flex flex-col gap-xs">
          {lanes.map((lane) => {
            // TES-1284 — a lab check waiting for its next time is not "now".
            const items = activeItems(laneItems(workQueue, lane)).sort(
              byUrgency,
            );
            return (
              <CategoryRow
                key={lane}
                label={LANE_LABEL[lane]}
                count={items.length}
                to={viewPath(LANE_PATH[lane])}
                top={items[0] ?? null}
              />
            );
          })}
          {readOnly ? null : (
            <Link
              to="/chat"
              className="flex items-center gap-md rounded-md border border-surfaceMuted bg-surface p-md transition hover:border-brand"
            >
              <span className="flex h-8 min-w-8 items-center justify-center rounded-full bg-brand/10 px-xs text-sm font-medium text-brand">
                {unread}
              </span>
              <span className="min-w-0 flex-1 text-sm font-medium sm:w-48 sm:flex-none">
                Chatt
              </span>
              <span className="hidden flex-1 text-sm text-textSecondary sm:block">
                {unread === 0
                  ? "Inga olästa patientmeddelanden."
                  : `${unread} oläst${unread === 1 ? "t" : "a"} meddelande${unread === 1 ? "" : "n"} från patienter.`}
              </span>
              <span className="shrink-0 text-sm text-brand">→</span>
            </Link>
          )}
        </div>
      </section>
    </div>
  );
}
